import {
  CalendarOutlined,
  ClockCircleOutlined,
  IssuesCloseOutlined,
  PushpinOutlined,
  TranslationOutlined,
} from '@ant-design/icons'
import { FORMAT_TIME_DEFAULT, LANGUAGE_CODE, LANGUAGE_DEFAULT, STATUS_DOCUMENT_SELECT } from '@src/configs/const.config'
import { ELanguage, EStatusDoc, TStatusDoc } from '@src/configs/interface.config'
import { getLocalStored } from '@src/libs/localStorage'
import { generateUrlFlagLang } from '@src/libs/translation'
import { TUser } from '@src/modules'
import {
  Badge,
  Button,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Image,
  Popconfirm,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useRef } from 'react'

import ModalCPNotify, { THandleModalCPNotify } from '../ModalCPNotify'

const { Panel } = Collapse

type TActionPublishData = {
  status?: EStatusDoc
  isActive?: boolean
  createdAt?: Date
  author?: TUser
  scheduleAt?: Date
  expiredAt?: Date
  publishedLanguage?: ELanguage[]
}

type TActionPublishShowInput = {
  isActive?: boolean
  status?: boolean
  scheduleAt?: boolean
  expiredAt?: boolean
  publishedLanguage?: boolean
}

export enum EModalNotify {
  GAME = 'GAME',
  POST = 'POST',
}

export type TDataNotify = {
  model: EModalNotify
  modelId: string
  [key: string]: any
}

type TActionPublish = {
  data?: TActionPublishData
  dataNotify?: TDataNotify
  statusOption?: TStatusDoc[]
  loadingPublish?: boolean
  loadingUpdate?: boolean
  loadingDelete?: boolean
  onPublish?: () => void
  onDelete?: () => void
  onUpdate?: () => void
  showInput?: TActionPublishShowInput
}

function ActionPublish({
  data,
  dataNotify,
  statusOption,
  loadingPublish = false,
  loadingUpdate = false,
  loadingDelete = false,
  onPublish,
  onDelete,
  onUpdate,
  showInput,
}: TActionPublish) {
  const lang = getLocalStored('lang')
  const modalCPNotifyRef = useRef<THandleModalCPNotify>(null)

  return (
    <>
      <Collapse defaultActiveKey={['1']} expandIconPosition="end">
        <Panel header="Action" key="1">
          <Row>
            <Col span={24} style={{ marginBottom: 24 }}>
              <Row>
                <Col flex="23px">
                  <Tooltip title="Created At">
                    <CalendarOutlined style={{ marginTop: 9 }} />
                  </Tooltip>
                </Col>
                <Col flex="1">
                  <DatePicker
                    disabled
                    showTime
                    allowClear={false}
                    format={FORMAT_TIME_DEFAULT}
                    className="date-picker-input"
                    defaultValue={dayjs(data?.createdAt || dayjs())}
                  />
                </Col>
              </Row>
            </Col>
            {!!showInput?.scheduleAt && (
              <Col span={24}>
                <Row>
                  <Col flex="23px">
                    <Tooltip title="Schedule At">
                      <ClockCircleOutlined style={{ marginTop: 9 }} />
                    </Tooltip>
                  </Col>
                  <Col flex="1">
                    <Form.Item name="scheduleAt" initialValue={dayjs(data?.scheduleAt || dayjs())}>
                      <DatePicker
                        showTime
                        allowClear={false}
                        format={FORMAT_TIME_DEFAULT}
                        className="date-picker-input"
                        placeholder="Schedule time"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            )}
            {!!showInput?.expiredAt && (
              <Col span={24}>
                <Row>
                  <Col flex="23px">
                    <Tooltip title="Expired time">
                      <IssuesCloseOutlined style={{ marginTop: 9 }} />
                    </Tooltip>
                  </Col>
                  <Col flex="1">
                    <Form.Item name="expiredAt" initialValue={dayjs(data?.expiredAt || dayjs().add(1, 'day'))}>
                      <DatePicker
                        showTime
                        allowClear={false}
                        format={FORMAT_TIME_DEFAULT}
                        className="date-picker-input"
                        placeholder="Expired time"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            )}
            {!!showInput?.isActive && (
              <Col span={24}>
                <Row>
                  <Col flex="23px">
                    <PushpinOutlined style={{ marginTop: 9 }} />
                  </Col>
                  <Col flex="1">
                    <Form.Item name="isActive" initialValue={!!data?.isActive}>
                      <Select>
                        <Select.Option value>
                          <Badge status="success" text="ACTIVE" />
                        </Select.Option>
                        <Select.Option value={false}>
                          <Badge status="error" text="INACTIVE" />
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            )}
            {!!showInput?.status && (
              <Col span={24}>
                <Row>
                  <Col flex="23px">
                    <PushpinOutlined style={{ marginTop: 9 }} />
                  </Col>
                  <Col flex="1">
                    <Form.Item name="status" initialValue={data?.status || EStatusDoc.DRAFT}>
                      <Select>
                        {(statusOption || STATUS_DOCUMENT_SELECT).map((item) => (
                          <Select.Option value={item.value} key={item.key}>
                            <Badge status={item.status} text={item.value.toLocaleUpperCase()} />
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            )}
            {!!showInput?.publishedLanguage && (
              <Col span={24}>
                <Row>
                  <Col flex="23px">
                    <TranslationOutlined style={{ marginTop: 9 }} />
                  </Col>
                  <Col flex="1">
                    <Form.Item
                      name="publishedLanguage"
                      initialValue={data?.publishedLanguage || lang ? [lang] : [LANGUAGE_DEFAULT]}
                    >
                      <Select mode="multiple">
                        {LANGUAGE_CODE.map((l) => (
                          <Select.Option value={l} key={l}>
                            <Space>
                              <Image preview={false} src={generateUrlFlagLang(l)} />
                              <Typography.Text>{l}</Typography.Text>
                            </Space>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            )}
          </Row>
          <Divider style={{ marginTop: 0, marginBottom: 18 }} />
          <Row justify="space-between">
            <Col>
              {data && onDelete && (
                <Popconfirm
                  placement="topRight"
                  title="Are you sure?"
                  onConfirm={() => {
                    if (onDelete) void onDelete()
                  }}
                >
                  <Button loading={loadingDelete}>Delete</Button>
                </Popconfirm>
              )}
            </Col>
            <Col>
              <Space>
                {dataNotify && (
                  <Button type="primary" ghost onClick={() => modalCPNotifyRef.current?.onOpen(true)}>
                    Send notify
                  </Button>
                )}
                {data ? (
                  <Button
                    type="primary"
                    loading={loadingUpdate}
                    onClick={() => {
                      if (onUpdate) void onUpdate()
                    }}
                  >
                    Update
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    loading={loadingPublish}
                    onClick={() => {
                      if (onPublish) void onPublish()
                    }}
                  >
                    Publish
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        </Panel>
      </Collapse>
      {dataNotify && <ModalCPNotify ref={modalCPNotifyRef} data={dataNotify} />}
    </>
  )
}

export default ActionPublish
